/**
 * The sources list shows a scene's items top-most first (the reverse of draw
 * order) with groups as collapsible subtrees. These helpers translate between
 * that presentation and the draw-order indices `commands.moveItem` expects.
 */
import type { Id, SceneItem } from '../../../core/model';
import type { DropPosition } from '../listMath';

export interface TreeRow {
  id: Id;
  depth: number;
  parentId: Id | null;
  type: SceneItem['type'];
  /** Only meaningful for groups. */
  collapsed: boolean;
  childCount: number;
}

export function flattenTree(items: readonly SceneItem[], collapsed: Readonly<Record<Id, true>>): TreeRow[] {
  const rows: TreeRow[] = [];
  const walk = (list: readonly SceneItem[], depth: number, parentId: Id | null): void => {
    for (let i = list.length - 1; i >= 0; i--) {
      const item = list[i]!;
      const isCollapsed = item.type === 'group' && collapsed[item.id] === true;
      rows.push({
        id: item.id,
        depth,
        parentId,
        type: item.type,
        collapsed: isCollapsed,
        childCount: item.type === 'group' ? item.children.length : 0,
      });
      if (item.type === 'group' && !isCollapsed) walk(item.children, depth + 1, item.id);
    }
  };
  walk(items, 0, null);
  return rows;
}

/** Changes only when the tree structure does, so a list can skip re-rendering on transform edits. */
export function treeSignature(items: readonly SceneItem[] | undefined): string {
  if (!items) return '';
  const part = (item: SceneItem): string =>
    item.type === 'group' ? `${item.id}[${item.children.map(part).join(',')}]` : item.id;
  return items.map(part).join(',');
}

export interface TreeNodeInfo {
  parentId: Id | null;
  /** Draw-order index inside the parent. */
  index: number;
  childCount: number;
  /** Group ids from the root down to the parent. */
  ancestors: Id[];
  isGroup: boolean;
}

export function indexTree(items: readonly SceneItem[]): Map<Id, TreeNodeInfo> {
  const map = new Map<Id, TreeNodeInfo>();
  const walk = (list: readonly SceneItem[], parentId: Id | null, ancestors: Id[]): void => {
    list.forEach((item, index) => {
      const isGroup = item.type === 'group';
      map.set(item.id, { parentId, index, childCount: isGroup ? item.children.length : 0, ancestors, isGroup });
      if (item.type === 'group') walk(item.children, item.id, [...ancestors, item.id]);
    });
  };
  walk(items, null, []);
  return map;
}

export interface MoveTarget {
  /** Destination container: a group id, or null for the scene root. */
  groupId: Id | null;
  /** Index in the destination after the dragged item has been taken out of its old place. */
  toIndex: number;
}

/**
 * Where `moveItem` should put `draggedId` when it is dropped on `targetId`.
 * 'before' means visually above the target (later in draw order), 'after'
 * visually below, 'inside' on top of a group's children. Returns null for
 * drops that make no sense (onto itself, into its own subtree, no change).
 */
export function resolveDrop(
  tree: ReadonlyMap<Id, TreeNodeInfo>,
  draggedId: Id,
  targetId: Id,
  position: DropPosition,
): MoveTarget | null {
  const dragged = tree.get(draggedId);
  const target = tree.get(targetId);
  if (!dragged || !target || draggedId === targetId) return null;
  if (target.ancestors.includes(draggedId)) return null;

  let groupId: Id | null;
  let insertion: number;
  if (position === 'inside') {
    if (!target.isGroup) return null;
    groupId = targetId;
    insertion = target.childCount;
  } else {
    groupId = target.parentId;
    insertion = position === 'before' ? target.index + 1 : target.index;
  }

  const sameParent = dragged.parentId === groupId;
  const toIndex = sameParent && dragged.index < insertion ? insertion - 1 : insertion;
  if (sameParent && toIndex === dragged.index) return null;
  return { groupId, toIndex };
}
